import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Loader2, Lock, Paperclip, ShieldAlert } from "lucide-react";
import { fmtDateTime } from "@/lib/time";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOG = {
  opened: (e) => `Case opened by ${e.by_name}`, note: (e) => `Note by ${e.by_name}`, status: (e) => e.text, reassigned: (e) => `Passed to ${e.to_name}`,
  linked: (e) => e.text, suspension: (e) => e.text, closed: (e) => `Closed by ${e.by_name}`, evidence: (e) => `Evidence added by ${e.by_name}`,
  shared: () => "Independent reviewer given access", share_revoked: () => "Reviewer access removed", reviewer_viewed: (e) => `${e.by_name} opened the case`, review_note: (e) => `Note by ${e.by_name}`,
};
const label = "mb-1.5 block text-[11px] font-bold uppercase tracking-[0.18em] text-[#3a3836]";
const detail = (err) => (typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "Something went wrong. Please try again.");

const Fact = ({ k, v }) => <div className="min-w-0"><dt className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#57534E]">{k}</dt><dd className="mt-0.5 break-words text-sm font-semibold">{v || "—"}</dd></div>;

export default function CaseReview() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [c, setC] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    axios.post(`${API}/case-review/open`, { token }).then((r) => setC(r.data)).catch((e) => setError(detail(e)));
  }, [token]);
  useEffect(() => { if (token) load(); else setError("This link isn't valid."); return undefined; }, [token, load]);
  const open = async (f) => {
    try {
      const r = await axios.get(`${API}/case-review/file`, { params: { token, fid: f.id }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); window.open(url, "_blank", "noopener"); setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError(detail(e)); }
  };
  const addNote = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/case-review/note`, { token, text: note }); setC(data); setNote(""); } catch (e) { setError(detail(e)); } finally { setBusy(false); }
  };
  if (!c) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] p-6 text-white" data-testid="case-review-page">
        {error ? <p className="max-w-md text-center text-sm" data-testid="case-review-error">{error}</p> : <Loader2 className="h-6 w-6 animate-spin" />}
      </div>
    );
  }
  const files = c.files || [];
  return (
    <div className="min-h-screen bg-[#F3F2EF] px-4 py-8 sm:px-8 sm:py-12" data-testid="case-review-page">
      <article className="mx-auto max-w-4xl border-2 border-[#0A0A0A] bg-[#FBF9F5]">
        <header className="bg-[#0A0A0A] p-5 text-white sm:p-7">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#ff6b5e]"><ShieldAlert className="h-4 w-4" />Confidential · independent review</p>
          <p className="mt-3 font-mono text-sm font-bold tracking-[0.2em]" data-testid="case-review-ref">{c.ref}</p>
          <h1 className="mt-2 break-words font-display text-2xl font-extrabold sm:text-3xl">{c.title}</h1>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-zinc-300"><Lock className="h-3.5 w-3.5" />Shared with {c.reviewer?.name} · access until {fmtDateTime(c.reviewer?.expires_at)}. Please don't share this page.</p>
        </header>
        <div className="space-y-6 p-5 sm:p-7">
          <dl className="grid grid-cols-2 gap-4 border border-black/15 bg-white p-4 sm:grid-cols-3">
            <Fact k="Type" v={c.type_label} /><Fact k="Status" v={c.status_label} /><Fact k="Opened" v={fmtDateTime(c.created_at)} />
            <Fact k="About" v={c.subject_name || "Not linked to an account"} /><Fact k="Handler" v={c.handler_name} />
            {c.complainant && <Fact k="Complainant" v={`${c.complainant.name || ""} · ${c.complainant.email || ""}`} />}
            {c.related_ref && <Fact k="Related reference" v={c.related_ref} />}
            {c.outcome_label && <Fact k="Outcome" v={c.outcome_label} />}
          </dl>
          <section><p className={label}>Summary</p><p className="whitespace-pre-wrap break-words border-l-4 border-[#E62314] bg-white p-4 text-sm leading-relaxed">{c.summary}</p></section>
          {files.length > 0 && (
            <section data-testid="case-review-evidence"><p className={label}>Evidence · {files.length}</p>
              <ul className="flex flex-wrap gap-1.5">{files.map((f) => <li key={f.id}><button onClick={() => open(f)} className="flex items-center gap-1.5 border border-black/20 bg-white px-2.5 py-1.5 text-xs font-semibold hover:border-black" data-testid={`case-review-file-${f.id}`}><Paperclip className="h-3.5 w-3.5" />{f.name}</button></li>)}</ul>
            </section>
          )}
          {c.reviewer?.can_note && c.status !== "closed" && (
            <section className="border border-black/20 bg-white p-4" data-testid="case-review-note">
              <p className={label}>Add your notes or findings</p>
              <textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} maxLength={5000} className="w-full border border-black/25 px-3 py-2.5 text-sm outline-none focus:border-black" data-testid="case-review-note-input" />
              <button disabled={!note.trim() || busy} onClick={addNote} className="mt-2 flex items-center gap-2 bg-[#0A0A0A] px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#E62314] disabled:opacity-40" data-testid="case-review-note-submit">{busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}Add to the case log</button>
            </section>
          )}
          {error && <p className="text-sm font-semibold text-[#C21B0E]" data-testid="case-review-error">{error}</p>}
          <section><p className={label}>Case log</p>
            <ol className="relative space-y-4 border-l-2 border-black/15 pl-5">
              {[...(c.events || [])].reverse().map((e) => (
                <li key={e.id} className="relative">
                  <span className="absolute -left-[27px] top-1 h-3 w-3 border-2 border-[#FBF9F5] bg-[#0A0A0A]" />
                  <p className="text-xs font-bold uppercase tracking-wider">{(LOG[e.kind] || (() => e.kind))(e)}</p>
                  <p className="text-xs text-[#57534E]">{fmtDateTime(e.at)}</p>
                  {e.text && ["note", "review_note", "evidence", "closed", "reassigned"].includes(e.kind) && <p className="mt-1 whitespace-pre-wrap break-words bg-white p-3 text-sm">{e.text}</p>}
                </li>
              ))}
            </ol>
          </section>
        </div>
      </article>
    </div>
  );
}
